/**
 * Turns the catalog response plus the fan's stored pin list into the exact
 * row the screen renders: `All` first, then the pinned leagues in the fan's
 * own order, then the leagues of the fan's favorite teams, then the league
 * being viewed if it is none of those.
 */
import {
  ALL_LEAGUES_LABEL,
  type typeLeagueSelectorOption,
  type typeLeagueSelectorRow,
} from "./leagueSelectorContract";

/** The catalog shape this module reads. A structural subset of
 *  POST /league-catalog/get's response, naming only the fields used. */
export type typeCatalogSportInput = {
  sportId: string;
  sportKey: string;
  name: string;
  leagues: {
    leagueId: string;
    leagueKey: string;
    name: string;
    shortName: string;
    visibilityState: string;
  }[];
};

export function buildLeagueSelectorRow(input: {
  sports: typeCatalogSportInput[];
  pinnedLeagueIds: string[];
  /** League ids resolved from the fan's favorite teams. Rendered after the
   *  pinned leagues, in catalog order, with the same pill. */
  favoriteLeagueIds?: readonly string[];
  activeLeagueId: string | null;
}): typeLeagueSelectorRow {
  const optionsByLeagueId = new Map<string, typeLeagueSelectorOption>();
  for (const sport of input.sports) {
    for (const league of sport.leagues) {
      optionsByLeagueId.set(league.leagueId, {
        leagueId: league.leagueId,
        leagueKey: league.leagueKey,
        name: league.name,
        shortName: league.shortName,
        isComingSoon: league.visibilityState === "comingSoon",
      });
    }
  }

  const emitted = new Set<string>();
  const pinned: (typeLeagueSelectorOption & { isActive: boolean })[] = [];
  for (const leagueId of input.pinnedLeagueIds) {
    const option = optionsByLeagueId.get(leagueId);
    if (option === undefined) continue;
    if (emitted.has(option.leagueId)) continue;
    emitted.add(option.leagueId);
    pinned.push({
      ...option,
      isActive: option.leagueId === input.activeLeagueId,
    });
  }

  // A favorited team keeps its league in the row even with no pin. Catalog
  // order, not favorite order, because a fan never orders their favorites.
  const favoriteLeagueIds = new Set(input.favoriteLeagueIds ?? []);
  for (const sport of input.sports) {
    for (const league of sport.leagues) {
      if (!favoriteLeagueIds.has(league.leagueId)) continue;
      if (emitted.has(league.leagueId)) continue;
      const option = optionsByLeagueId.get(league.leagueId);
      if (option === undefined) continue;
      emitted.add(option.leagueId);
      pinned.push({
        ...option,
        isActive: option.leagueId === input.activeLeagueId,
      });
    }
  }

  // The league being viewed gets a chip, even with no pin and no favorite
  // team in it, as long as the catalog resolves its id (ISSUE-949 R2). A fan
  // reaches a league from the league menu sheet or a shared link just as often
  // as from their own pins, and without this the chip row on that screen
  // carries no active chip at all, which reads as broken on a row that looks
  // like a tab bar. An id the catalog does not resolve adds no chip, so that
  // row marks nothing and `All` is inactive too.
  //
  // It trails the row rather than leading it (ISSUE-949 OD-1). Every chip the
  // `All` feed shows keeps its exact position, and removing this chip when
  // the fan moves on never shifts a chip to its left. `LeagueSelectorRow`
  // scrolls the active chip into view, so a trailing chip is not lost off the
  // right edge. The chip exists only while that league is active, so the
  // fan's pinned order is never rewritten.
  const active: (typeLeagueSelectorOption & { isActive: boolean })[] = [];
  if (input.activeLeagueId !== null && !emitted.has(input.activeLeagueId)) {
    const option = optionsByLeagueId.get(input.activeLeagueId);
    if (option !== undefined) {
      emitted.add(option.leagueId);
      active.push({ ...option, isActive: true });
    }
  }

  return {
    all: {
      label: ALL_LEAGUES_LABEL,
      isActive: input.activeLeagueId === null,
    },
    pinned: [...pinned, ...active],
  };
}
